// TelcoTrack — contract fill fields sourced from stavba (polygon) Specifikace.

import type { BremenaInstrumentFillData } from './bremena-instrument-fill.types';

/** Specifikace stavby / polygon fields used for CETIN contract fill. */
export interface BremenaStavbaSpecFields {
  sapKod?: string | null;
  kiaKod?: string | null;
  /** Název stavby (`polygons.name`) — fallback for označení. */
  polygonName?: string | null;
  obec?: string | null;
  okres?: string | null;
}

function trimOrUndefined(value: unknown): string | undefined {
  if (value == null) return undefined;
  const s = String(value).trim();
  return s || undefined;
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = trimOrUndefined(value);
    if (trimmed) return trimmed;
  }
  return undefined;
}

/**
 * Označení stavby: explicit value → polygon name → KIA.
 * Explicit value is typically `fill_data.oznaceniStavby` (optional override).
 */
export function resolveBremenaOznaceniStavby(args: {
  oznaceniStavby?: string | null;
  polygonName?: string | null;
  kiaKod?: string | null;
}): string | undefined {
  return firstNonEmpty(args.oznaceniStavby, args.polygonName, args.kiaKod);
}

export type BremenaStavbaFillResolved = Pick<
  BremenaInstrumentFillData,
  'sapKod' | 'kiaKod' | 'oznaceniStavby' | 'obec' | 'okres'
>;

/**
 * Resolve SAP / KIA / označení / obec / okres for contract generate + fill modal.
 *
 * Priority: `fill_data` override → instrument columns (SAP/KIA) → stavba Specifikace.
 * Označení: fill → polygon name → resolved KIA.
 */
export function applyBremenaStavbaSpecDefaults(
  fill: BremenaInstrumentFillData | null | undefined,
  instrument: { sapCode?: string | null; kiaCode?: string | null } | null | undefined,
  stavba: BremenaStavbaSpecFields | null | undefined,
): BremenaStavbaFillResolved {
  const sapKod = firstNonEmpty(fill?.sapKod, instrument?.sapCode, stavba?.sapKod);
  const kiaKod = firstNonEmpty(fill?.kiaKod, instrument?.kiaCode, stavba?.kiaKod);
  const oznaceniStavby = resolveBremenaOznaceniStavby({
    oznaceniStavby: fill?.oznaceniStavby,
    polygonName: stavba?.polygonName,
    kiaKod,
  });
  const obec = firstNonEmpty(fill?.obec, stavba?.obec);
  const okres = firstNonEmpty(fill?.okres, stavba?.okres);
  return {
    ...(sapKod ? { sapKod } : {}),
    ...(kiaKod ? { kiaKod } : {}),
    ...(oznaceniStavby ? { oznaceniStavby } : {}),
    ...(obec ? { obec } : {}),
    ...(okres ? { okres } : {}),
  };
}

export type BremenaStavbaFillKey = keyof BremenaStavbaFillResolved;

const STAVBA_FILL_KEYS: readonly BremenaStavbaFillKey[] = [
  'sapKod',
  'kiaKod',
  'oznaceniStavby',
  'obec',
  'okres',
];

/**
 * Fields still missing after stavba Specifikace (+ instrument SAP/KIA columns).
 * Ignores `fill_data` — used by Doplnit modal to decide which stavba fields to show/require.
 */
export function bremenaStavbaFieldsMissingFromSpec(
  instrument: { sapCode?: string | null; kiaCode?: string | null } | null | undefined,
  stavba: BremenaStavbaSpecFields | null | undefined,
): BremenaStavbaFillKey[] {
  const fromSpec = applyBremenaStavbaSpecDefaults(null, instrument, stavba);
  return STAVBA_FILL_KEYS.filter((key) => !fromSpec[key]);
}
